import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/api";
import { Trash2, Plus, Minus } from "lucide-react";

export default function CartDrawer() {
  const { items, open, setOpen, remove, updateQty, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full sm:w-[420px] bg-white flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-heading text-slate-900">Your Cart</SheetTitle>
        </SheetHeader>
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <div className="text-slate-500 mb-4">Your cart is empty.</div>
            <Button onClick={() => { setOpen(false); navigate("/shop"); }} data-testid="cart-drawer-shop">
              Browse products
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100 -mx-6 px-6 mt-4">
              {items.map((it) => (
                <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="py-4 flex gap-3">
                  <Link to={`/product/${it.slug}`} onClick={() => setOpen(false)} className="w-20 h-20 bg-slate-50 rounded-md overflow-hidden shrink-0">
                    {it.image && <img src={it.image} alt="" className="w-full h-full object-contain" />}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/product/${it.slug}`} onClick={() => setOpen(false)} className="text-sm text-slate-900 line-clamp-2 hover:text-[#0055FF]">
                      {it.title}
                    </Link>
                    <div className="text-xs font-mono text-slate-400 mt-1">{it.sku}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-slate-200 rounded-md">
                        <button data-testid={`cart-dec-${it.product_id}`} aria-label="Decrease quantity" onClick={() => updateQty(it.product_id, it.quantity - 1)} className="w-7 h-7 flex items-center justify-center hover:bg-slate-100">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-mono">{it.quantity}</span>
                        <button data-testid={`cart-inc-${it.product_id}`} aria-label="Increase quantity" onClick={() => updateQty(it.product_id, it.quantity + 1)} className="w-7 h-7 flex items-center justify-center hover:bg-slate-100">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-sm font-semibold text-slate-900">{formatPrice(it.price * it.quantity)}</div>
                    </div>
                  </div>
                  <button data-testid={`cart-remove-${it.product_id}`} aria-label="Remove item" onClick={() => remove(it.product_id)} className="text-slate-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 pt-4 mt-2 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-heading font-semibold text-slate-900">{formatPrice(subtotal)}</span>
              </div>
              <p className="text-xs text-slate-500">Shipping and tax calculated at checkout. Free shipping on orders over $150.</p>
              <Button data-testid="cart-view-cart-button" variant="outline" className="w-full" onClick={() => { setOpen(false); navigate("/cart"); }}>
                View cart
              </Button>
              <Button data-testid="cart-checkout-button" className="w-full bg-[#0055FF] hover:bg-[#0044CC]" onClick={() => { setOpen(false); navigate("/cart?checkout=1"); }}>
                Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
